import { useState, useMemo } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import Logo from "@/components/Logo";
import { api } from "@/lib/api";
import { toast } from "sonner";

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = useMemo(() => searchParams.get("token") || "", [searchParams]);
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error("Missing reset token. Open the link from your email again.");
      return;
    }
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords don't match.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      setDone(true);
      toast.success("Password updated. Sign in below.");
      setTimeout(() => navigate("/login"), 1500);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not reset password.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-mm-parchment bg-topo flex flex-col" data-testid="reset-password-page">
      <div className="container mx-auto py-6">
        <Link to="/"><Logo size={44} /></Link>
      </div>
      <div className="flex-1 flex items-center justify-center px-4">
        <div className="w-full max-w-md bg-white border border-mm-navy/10 rounded-2xl p-8 fade-up">
          <div className="text-xs uppercase tracking-[0.22em] font-bold text-mm-steel">Set new password</div>
          <h1 className="font-heading text-3xl font-black text-mm-navy mt-2">Pick a fresh waypoint</h1>
          <p className="text-mm-navy/70 mt-2 text-sm">
            Choose a new password to get back on the mountain. At least 8 characters.
          </p>

          {!token && (
            <div className="mt-6 rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-800" data-testid="reset-password-missing-token">
              This link is missing its security token. Please request a new reset email from{" "}
              <Link to="/forgot-password" className="font-bold underline">Forgot password</Link>.
            </div>
          )}

          {done ? (
            <div className="mt-6 rounded-lg bg-mm-parchment/60 border border-mm-navy/10 p-4" data-testid="reset-password-success-state">
              <div className="font-bold text-mm-navy">Password updated</div>
              <p className="text-sm text-mm-navy/70 mt-1">Redirecting you to the trailhead…</p>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <label className="text-xs uppercase tracking-widest font-bold text-mm-steel">New password</label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                  disabled={!token}
                  className="mt-1.5 w-full bg-white border-2 border-mm-navy/15 rounded-lg px-4 py-3 focus:outline-none focus:border-mm-steel focus:ring-2 focus:ring-mm-steel/30 disabled:opacity-50"
                  data-testid="reset-password-new-input"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest font-bold text-mm-steel">Confirm password</label>
                <input
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  minLength={8}
                  disabled={!token}
                  className="mt-1.5 w-full bg-white border-2 border-mm-navy/15 rounded-lg px-4 py-3 focus:outline-none focus:border-mm-steel focus:ring-2 focus:ring-mm-steel/30 disabled:opacity-50"
                  data-testid="reset-password-confirm-input"
                />
              </div>
              <button type="submit" disabled={loading || !token} className="btn-trail btn-gold w-full mt-2" data-testid="reset-password-submit-btn">
                {loading ? "Securing your camp..." : "Update password"}
              </button>
            </form>
          )}

          <div className="text-sm text-mm-navy/70 mt-6 text-center">
            <Link to="/login" className="font-bold text-mm-navy hover:text-mm-steel" data-testid="reset-password-to-login-link">
              Back to login
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
